/**
 * Resolve active company↔project participation for stakeholder chrome
 * (sidebar identity + company-portal dashboard title).
 *
 * Display-only: uses the same project-scope precedence as sidebar identity.
 */

import type { User } from '@/types';
import { getShowcaseProject } from '@/demo/showcaseCatalog';
import { getCompanyLinkedProjects, getProjectLinkedCompanies } from '@/demo/demoStore';
import { getRepresentedCompanyIds } from '@/lib/auth/companyPortal';
import { getRepresentedRelationshipCompanyIds } from '@/lib/auth/scopedRelationshipResolver';
import {
  deriveCompanyParticipation,
  type CompanyProjectParticipation,
} from '@/types/projectParticipants';
import {
  resolveSidebarRouteProjectId,
  resolveSidebarScopedProjectId,
} from '@/lib/layout/resolvePortalSidebarIdentity';

export interface StakeholderParticipationContext {
  projectId: string | null;
  projectName: string | null;
  representedCompanyId: string | null;
  companyParticipation: CompanyProjectParticipation | null;
}

/**
 * Active project + company↔project participation for the signed-in representative.
 *
 * Precedence for project id mirrors sidebar identity:
 *   route `/projects/:id` → project-scoped representation → single linked project.
 */
export function resolveStakeholderParticipationContext(
  user: User | null | undefined,
  pathname: string | null | undefined = null,
  preferredCompanyId: string | null | undefined = null,
): StakeholderParticipationContext {
  const routeProjectId = resolveSidebarRouteProjectId(pathname);
  const representedCompanyId =
    preferredCompanyId?.trim()
    || getRepresentedCompanyIds(user)[0]
    || getRepresentedRelationshipCompanyIds(user, 'partner_representative')[0]
    || getRepresentedRelationshipCompanyIds(user, 'vendor_representative')[0]
    || null;

  let projectId = resolveSidebarScopedProjectId(user, routeProjectId);

  if (!projectId && representedCompanyId) {
    const linkedProjects = getCompanyLinkedProjects(representedCompanyId);
    if (linkedProjects.length === 1) {
      projectId = linkedProjects[0].project_id;
    }
  }

  const projectName = projectId
    ? (getShowcaseProject(projectId)?.name ?? null)
    : null;

  let companyParticipation: CompanyProjectParticipation | null = null;
  if (projectId && representedCompanyId) {
    const link = getProjectLinkedCompanies(projectId).find(
      (row) => row.company_id === representedCompanyId && row.status === 'active',
    );
    companyParticipation = deriveCompanyParticipation(link?.relationship_type);
  }

  return {
    projectId,
    projectName,
    representedCompanyId,
    companyParticipation,
  };
}
